/**
 * /contact — public quote-request form (BCOS §11 / Slice 10 of the
 * make-mocks-real plan).
 *
 * P1 fix (2026-07-30, forensic sweep leg 1 Classes 1/4/6): this page used
 * to validate locally, write the submission to `localStorage`, and show
 * the real "Thanks" success card WITHOUT ever calling the backend — a
 * silent-lead-loss bug. The named blocker (`POST
 * /public/tenants/{tenant_uid}/contact-submissions`) has been live since
 * `backend/routes/contact_submissions.py` shipped; this page just never
 * called it. It now does.
 *
 * `tenant_uid` identifies which GC's pipeline the submission routes into
 * (tenant routing is still by explicit parameter — subdomain routing is
 * A8.x). It hydrates from `?tenant_uid=` on the URL, same as the UTM /
 * tracked-link attribution fields, so a GC's own tracked "get a quote"
 * link can carry it.
 *
 * Default-entry-point repair (2026-07-31, PR #2507 cross-model HOLD
 * verdict): the P1 fix above correctly refused to fake-submit when
 * `tenant_uid` is missing — but the app-wide footer's `<a href="/contact">`
 * (`web/viewer/src/app-chrome.ts` `buildFooter`, rendered on every
 * authenticated page) always lands here with NO tenant_uid, so that
 * everyday entry point dead-ended on an honest error with nothing the
 * visitor could actually do. There is no legitimate site-default tenant
 * to fall back to (verified against `backend/routes/contact_submissions.py`
 * `_require_existing_tenant` + `MembershipRepo` — TradesView operates no
 * ACTIVE `hub_owner` tenant of its own, and the shipped `designer-test`
 * tenant is the protected demo account, never a production default; see
 * the PR body for the full trace). So the no-tenant_uid path is handled
 * at PAGE LOAD, before the form even renders: it shows a real, working
 * `mailto:hello@tradesview.ai` CTA instead — the same honest-empty
 * pattern already shipped for "no phone number connected yet"
 * (`web/viewer/src/playbook-authoring.ts` `noNumberBannerHtml`). This
 * never claims TradesView received anything (nothing was stored) — it
 * just makes the default click-through actually DO something real.
 *
 * The success card renders ONLY on a real 2xx response. Any error —
 * network failure, timeout, or a non-2xx status — shows a visible error
 * message, leaves the typed fields exactly as the visitor left them (the
 * form is never cleared or reset on failure), and re-enables the submit
 * button so retry is just "click Send again." A local `tv.contact.draft`
 * copy is kept purely as a crash/reload recovery aid — it is never shown
 * as, or treated as, a successful submission.
 */

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://tradesview-7hynw6lk4q-uc.a.run.app";

const DRAFT_STORAGE_KEY = "tv.contact.draft";
const SUBMIT_TIMEOUT_MS = 15_000;
const GENERAL_INQUIRY_EMAIL = "hello@tradesview.ai";

const formEl               = document.getElementById("contact-form") as HTMLFormElement;
const statusEl             = document.getElementById("cf-status") as HTMLElement;
const submitBtn            = document.getElementById("cf-submit-btn") as HTMLButtonElement;
const thanksCardEl         = document.getElementById("thanks-card") as HTMLElement;
const generalInquiryCardEl = document.getElementById("general-inquiry-card") as HTMLElement;
const generalInquiryLinkEl = document.getElementById("general-inquiry-mailto") as HTMLAnchorElement;

// Fields that ride along on the request body — mirrors
// `CreateContactSubmissionPublicRequest` in
// backend/routes/contact_submissions.py exactly. `tenant_uid` is
// deliberately excluded: it's a URL path parameter, not a body field.
const BODY_FIELDS = [
  "first_name", "last_name", "email", "phone", "address",
  "project_type", "message",
  "utm_source", "utm_medium", "utm_campaign", "tracked_link_click_id",
] as const;

// Hidden attribution fields hydrated from URL query string. UTM ride-along
// works exactly like the real backend will: a /go/<short_code> redirect
// (Slice 10.2) lands the homeowner on
// /contact?tenant_uid=...&utm_source=...&utm_medium=... and the form
// forwards everything to the submission record.
function hydrateAttributionFields(): void {
  const params = new URLSearchParams(window.location.search);
  const setIfPresent = (id: string, key: string) => {
    const v = params.get(key);
    if (v) (document.getElementById(id) as HTMLInputElement).value = v;
  };
  setIfPresent("cf-tenant-uid",    "tenant_uid");
  setIfPresent("cf-utm-source",    "utm_source");
  setIfPresent("cf-utm-medium",    "utm_medium");
  setIfPresent("cf-utm-campaign",  "utm_campaign");
  setIfPresent("cf-tracked-click", "tracked_link_click_id");
}

function showStatus(text: string, kind: "error" | "success"): void {
  statusEl.className = `form-status ${kind}`;
  statusEl.textContent = text;
}

function hideStatus(): void {
  statusEl.className = "form-status hidden";
  statusEl.textContent = "";
}

function showThanks(): void {
  formEl.style.display = "none";
  thanksCardEl.style.display = "block";
}

// Honest default-entry-point CTA (see file header, PR #2507 repair). Builds
// a real mailto: link — pre-filled with whatever the visitor already typed,
// when there's anything to carry over — so the app-wide footer "Contact"
// link (and any other no-tenant_uid hit) resolves to a working action
// instead of a dead end. Never claims TradesView received or stored
// anything; the visitor's own email client is what actually sends it.
function buildGeneralInquiryMailto(payload: Record<string, string>): string {
  const subject = encodeURIComponent("Project inquiry via tradesview.ai");
  const name = [payload.first_name, payload.last_name].filter(Boolean).join(" ");
  const lines = [
    name ? `Name: ${name}` : null,
    payload.email ? `Email: ${payload.email}` : null,
    payload.phone ? `Phone: ${payload.phone}` : null,
    payload.address ? `Address: ${payload.address}` : null,
    payload.project_type ? `Project type: ${payload.project_type}` : null,
    payload.message ? `\n${payload.message}` : null,
  ].filter((l): l is string => l !== null);
  const body = encodeURIComponent(lines.join("\n"));
  return `mailto:${GENERAL_INQUIRY_EMAIL}?subject=${subject}&body=${body}`;
}

function showGeneralInquiry(payload: Record<string, string> = {}): void {
  generalInquiryLinkEl.href = buildGeneralInquiryMailto(payload);
  formEl.style.display = "none";
  thanksCardEl.style.display = "none";
  generalInquiryCardEl.style.display = "block";
}

function readFormData(): Record<string, string> {
  const fd = new FormData(formEl);
  const out: Record<string, string> = {};
  for (const [k, v] of fd.entries()) {
    const sv = String(v).trim();
    if (sv) out[k] = sv;
  }
  return out;
}

function validateLocally(payload: Record<string, string>): string | null {
  if (!payload.first_name)   return "Please enter your first name.";
  if (!payload.email)        return "Please enter your email address.";
  if (!payload.phone)        return "Please enter a phone number.";
  if (!payload.project_type) return "Please choose a project type.";
  if (!payload.message)      return "Please tell us a little about your project.";
  // Loose email check — backend Pydantic validation is the source of
  // truth on the actual submit.
  if (!/.+@.+\..+/.test(payload.email)) {
    return "That email doesn't look right. Please double-check.";
  }
  return null;
}

// RECOVERY AID ONLY — never a stand-in for a real submission. Lets a
// visitor who reloads mid-fill (or whose submit failed and closed the
// tab) get their typed content back; it makes no claim of having reached
// the GC.
function saveDraft(payload: Record<string, string>): void {
  try {
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      saved_at: new Date().toISOString(),
      payload,
    }));
  } catch {
    // Quota / private-mode failure — non-fatal, the draft is best-effort.
  }
}

function clearDraft(): void {
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
}

function buildRequestBody(payload: Record<string, string>): Record<string, string> {
  const body: Record<string, string> = {};
  for (const key of BODY_FIELDS) {
    if (payload[key]) body[key] = payload[key];
  }
  return body;
}

async function extractErrorDetail(res: Response): Promise<string | null> {
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") return data.detail;
    if (Array.isArray(data?.detail) && data.detail.length > 0) {
      // FastAPI/Pydantic 422 shape: [{ loc, msg, type }, ...]
      const first = data.detail[0];
      if (typeof first?.msg === "string") return first.msg;
    }
  } catch {
    // Body wasn't JSON — fall through to a generic message.
  }
  return null;
}

async function submitContactForm(ev: SubmitEvent): Promise<void> {
  ev.preventDefault();
  hideStatus();

  const payload = readFormData();
  const validationError = validateLocally(payload);
  if (validationError) {
    showStatus(validationError, "error");
    return;
  }

  const tenantUid = payload.tenant_uid;
  if (!tenantUid) {
    // Defense-in-depth: the page-load check in init() already swaps to the
    // general-inquiry card before the form is ever shown when tenant_uid is
    // missing, so this should be unreachable in practice. If it's somehow
    // hit anyway (e.g. the hidden field was cleared client-side), fall back
    // to the same honest, working mailto: CTA rather than a dead-end error.
    saveDraft(payload);
    showGeneralInquiry(payload);
    return;
  }

  submitBtn.disabled = true;
  submitBtn.textContent = "Sending…";
  saveDraft(payload);

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

  try {
    const res = await fetch(
      `${API_BASE}/public/tenants/${encodeURIComponent(tenantUid)}/contact-submissions`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildRequestBody(payload)),
        signal: controller.signal,
      },
    );

    if (!res.ok) {
      const detail = await extractErrorDetail(res);
      showStatus(
        detail
          ? `We couldn't send your message: ${detail}. Your info is still here — please try again.`
          : "Something went wrong sending your message. Your info is still here — please try again.",
        "error",
      );
      return;
    }

    // Real success — 2xx from the live backend. The draft has served its
    // purpose; clear it so a later visit doesn't offer a stale recovery.
    clearDraft();
    showThanks();
  } catch (err) {
    const timedOut = err instanceof DOMException && err.name === "AbortError";
    showStatus(
      timedOut
        ? "That took too long to send. Your info is still here — please try again."
        : "We couldn't reach TradesView. Check your connection — your info is still here, please try again.",
      "error",
    );
    // eslint-disable-next-line no-console
    console.warn("contact-form submit failed", err);
  } finally {
    window.clearTimeout(timeoutId);
    submitBtn.disabled = false;
    submitBtn.textContent = "Send message";
  }
}

function init(): void {
  const tenantUid = new URLSearchParams(window.location.search).get("tenant_uid");
  if (!tenantUid) {
    // No workspace to submit into (e.g. the app-wide footer "Contact"
    // link — see file header). Show the honest, working mailto: CTA
    // immediately instead of asking the visitor to fill out a project
    // form that dead-ends at submit.
    showGeneralInquiry();
    return;
  }
  formEl.addEventListener("submit", (ev) => { void submitContactForm(ev); });
  hydrateAttributionFields();
}

init();
